/* ===================================================================
   useSubscriptionAccess() -- the ONE decision for "can this account use the
   product?" (2026-07-06 monetization gate, Brad).

   Before this, the app gated on a Supabase SESSION only (RequireAuth): any
   logged-in account reached the whole product without paying. This joins the
   session gate with the subscription bool from GET /api/v1/billing/plan (via
   usePlan) + the operator whitelist (getAdminMe.is_admin, the same signal the
   sidebar's "Operator console" link already uses) into a single access state.

   Access model (brief 2026-07-06, single source of truth = /billing/plan):
     loading      -> hold (render nothing, don't flash the gate)
     whitelisted  -> pass  (operator/admin, and demo -- see TODO below)
     trial/active -> pass  (has_active_subscription === true)
     none/expired -> gate  (-> /choose-plan)

   The load-bearing field is has_active_subscription (TRUE only for trial +
   active-in-good-standing). `mode` ('new' vs 'resubscribe') only drives COPY
   on the plan picker, never access.
   =================================================================== */
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { usePlan } from './usePlan';
import { useMe } from './useMe';
import { useApiToken } from './useApiToken';

export type AccessState = 'loading' | 'allowed' | 'gated';
/* 'new' = never subscribed -> "Start your 14-day trial".
   'resubscribe' = had a prior sub that lapsed -> "Your trial has ended". */
export type ChoosePlanMode = 'new' | 'resubscribe';

export interface SubscriptionAccess {
  state: AccessState;
  /* True only for the operator/admin (and, once wired, the demo tenant)
     whitelist -- they bypass the gate entirely. */
  whitelisted: boolean;
  /* Copy mode for the /choose-plan screen. Meaningful only when state==='gated'
     (or on the plan picker generally). */
  mode: ChoosePlanMode;
}

export function useSubscriptionAccess(): SubscriptionAccess {
  const hasToken = Boolean(useApiToken());
  const plan = usePlan();
  const me = useMe();

  /* Operator/admin whitelist. Same query key + shape as AppShell + Agency +
     PostAuthLanding, so the cache is shared (no extra request) and a 401/403/
     error simply resolves to is_admin !== true (fail closed for the WHITELIST,
     which is the safe direction -- a non-admin just falls through to the normal
     subscription gate). retry:false so a non-admin's 403 doesn't spin. */
  const adminMeQ = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.getAdminMe(signal),
    enabled: hasToken,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const isOperator = adminMeQ.data?.is_admin === true;

  /* Demo-tenant whitelist. Hopper's /me now returns is_demo_tenant for the
     seeded public demo login (engine/api/v1/core.py::_is_demo_tenant), so the
     demo account un-gates even if its seeded subscription lapses. */
  const isDemo = me.data?.is_demo_tenant === true;

  const whitelisted = isOperator || isDemo;

  /* Checkout-return grace: the Stripe success redirect lands on
     /settings?billing=success while the ['billing-plan'] cache may still say
     has_active_subscription=false (the webhook that flips the bool can lag a
     beat). Treat that one landing as allow-through so the just-subscribed user
     is NOT bounced to /choose-plan; App-level CheckoutReturnSync invalidates
     the plan so the next read is fresh. Scoped to the success param only. */
  const justCheckedOut =
    new URLSearchParams(window.location.search).get('billing') === 'success';

  // Copy mode: prefer the backend hint; fall back to `status` so we still say
  // "resubscribe" for a lapsed sub before Hopper ships had_prior_subscription.
  const status = (plan.data?.status ?? '').toLowerCase();
  const lapsed =
    plan.data?.had_prior_subscription === true ||
    status === 'expired' ||
    status === 'canceled' ||
    status === 'cancelled' ||
    status === 'past_due';
  const mode: ChoosePlanMode = lapsed ? 'resubscribe' : 'new';

  // ---- Resolve the access state ----
  // Whitelisted operators/demo pass immediately -- don't even wait on the plan.
  if (whitelisted) return { state: 'allowed', whitelisted: true, mode };

  // Just returned from a successful Stripe checkout -> allow through this
  // render (see justCheckedOut above); the plan refetch catches up right after.
  if (justCheckedOut) return { state: 'allowed', whitelisted: false, mode };

  // Hold until the plan query has resolved (success or error). While the admin
  // check is still in flight AND could still flip us to whitelisted, we also
  // hold, so an operator never flashes the gate on a cold load.
  const adminSettled = adminMeQ.isSuccess || adminMeQ.isError || !hasToken;
  // /me carries the demo-tenant whitelist flag; wait on it too so a demo
  // account is never briefly gated before is_demo_tenant resolves. Same
  // qk.me() query AppShell fires -> shared cache, no extra request.
  const meSettled = me.isSuccess || me.isError || !hasToken;
  const planSettled = plan.isSuccess || plan.isError;
  if (!adminSettled || !meSettled || !planSettled) {
    return { state: 'loading', whitelisted: false, mode };
  }

  // If the plan call errored we FAIL OPEN to avoid locking out a paying
  // customer on a transient billing-service blip. The TrialBar + Billing panel
  // still nudge; a hard lock on a network error would be worse than a brief
  // free ride. (has_active_subscription is the enforcement when it DID load.)
  if (plan.isError) return { state: 'allowed', whitelisted: false, mode };

  const active = plan.data?.has_active_subscription === true;
  return { state: active ? 'allowed' : 'gated', whitelisted: false, mode };
}
